import React, { FC, forwardRef, useImperativeHandle, useState } from 'react'
import { Column } from 'react-table'
import { t, Trans } from '@lingui/macro'

import Table from '../../../components/common/table/Table'
import UserDetail from '../../../components/common/user/user-detail/UserDetail'
import {
  OrderDescriptorWorkspaceUsersWithRole,
  SqlOrderDirectionEnum,
  UserModelWithWorkspaceRole,
  WorkspaceRoleEnum
} from '../../../graphql/private/PrivateSchemaTypes'
import IconButton from '../../../components/common/form/button/IconButton'
import IconBan from '../../../components/common/icon/IconBan'
import {
  useRemoveUserFromWorkspaceAccessMutation,
  useSetUserRoleForWorkspaceMutation
} from './WorkspaceMembersTab.codegen'
import { mapRolesToSelectOptions } from './WorkspaceMembersTab'
import TableExtendedOld, {
  DEFAULT_PAGE_LIMIT
} from '../../../components/common/table-extended-old/TableExtendedOld'
import { useWorkspaceUsersQuery } from './WorkspaceUsers.codegen'
import { getPagesCount } from '../../analytics/utils'
import { Select } from 'front-end/src/components'

export const WorkspaceUsers: FC<{
  workspaceId: number
}> = forwardRef(({ workspaceId }, ref) => {
  const [removeUser] = useRemoveUserFromWorkspaceAccessMutation()
  const [setUserRole] = useSetUserRoleForWorkspaceMutation()
  const [tableFilters, setTableFilters] = useState({
    page: 1,
    order: { name: 'display_name', sort: SqlOrderDirectionEnum.ASC },
    search: ''
  })

  const { data, loading, refetch } = useWorkspaceUsersQuery({
    variables: {
      workspaceId,
      page: tableFilters.page,
      limit: DEFAULT_PAGE_LIMIT,
      order: tableFilters.order as OrderDescriptorWorkspaceUsersWithRole,
      search: tableFilters.search
    }
  })

  useImperativeHandle(ref, () => ({
    onUsersChange() {
      refetch()
    }
  }))

  let usersWithRole: Pick<
    UserModelWithWorkspaceRole,
    'id' | 'workspaceRole' | 'userIdentity' | 'display_name' | 'image_url'
  >[] = []
  let pagesCount = 1
  if (
    data?.workspace?.__typename === 'WorkspaceModelAsCoordinator' &&
    data?.workspace?.usersWithRole?.count
  ) {
    pagesCount = getPagesCount(
      data.workspace.usersWithRole.count,
      DEFAULT_PAGE_LIMIT
    )
    usersWithRole = data.workspace.usersWithRole.items
  }

  const roles = mapRolesToSelectOptions()

  const tableColumns: Column[] = [
    {
      Header: t`Name`,
      sortable: true,
      accessor: 'display_name',
      Cell: (row) => {
        return (
          <UserDetail
            display_name={row.original.display_name}
            meta={row.original.userIdentity}
            imageUrl={row.original.image_url}
          />
        )
      }
    },
    {
      Header: t`Access rights`,
      sortable: false,
      accessor: 'rights',
      minWidth: 200,
      Cell: (row) => {
        return (
          <Select
            controlWidth={320}
            title={t`Permissions`}
            options={roles}
            value={roles.find(({ value }) => {
              return value === row.original.workspaceRole
            })}
            onChange={async (v) => {
              await setUserRole({
                variables: {
                  workspaceId,
                  userId: row.original.id,
                  role: v?.value as WorkspaceRoleEnum
                }
              })
              refetch()
            }}
          />
        )
      }
    },
    {
      sortable: false,
      width: 40,
      accessor: 'actions',
      Cell: (row) => {
        return (
          <IconButton
            icon={<IconBan />}
            hint={t`Remove access`}
            onClick={async () => {
              await removeUser({
                variables: {
                  workspaceId,
                  userId: row.original.id
                }
              })
              refetch()
            }}
          />
        )
      }
    }
  ]

  return (
    <>
      <Table className="TableMembers">
        <TableExtendedOld
          tableHeader={<Trans>People</Trans>}
          tableDescription={
            <Trans>
              Give individuals specific permissions in your workspace.
            </Trans>
          }
          loading={loading}
          manual
          data={usersWithRole}
          columns={tableColumns}
          className="-highlight AnalyticsTable"
          defaultPageSize={DEFAULT_PAGE_LIMIT}
          showPageSizeOptions={false}
          resizable={false}
          minRows={1}
          placeholder={t`No users yet`}
          pages={pagesCount}
          showSearchFilter
          onPageChange={(newPage) => {
            setTableFilters((prevState) => {
              return {
                ...prevState,
                page: newPage + 1
              }
            })
          }}
          page={tableFilters.page - 1}
          defaultSortDesc
          onSortedChange={(newSorted) => {
            setTableFilters((prevState) => {
              return {
                ...prevState,
                order: {
                  name: newSorted[0].id,
                  sort: newSorted[0].desc
                    ? SqlOrderDirectionEnum.DESC
                    : SqlOrderDirectionEnum.ASC
                }
              }
            })
          }}
          onSearchChange={(search) => {
            setTableFilters((prevState) => {
              return {
                ...prevState,
                search
              }
            })
          }}
        />
      </Table>
    </>
  )
})
